import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import {
  useGetEventsQuery,
  useSelfCheckInEventMutation,
} from "../../../features/Services/eventsServices";

// EventGeofencePrompt
//
// Mounted in the authed shell. Once per portal load, after the events list
// is fetched, finds any event whose start/end window is active (with the
// same grace the BFF uses) AND that has a Venue Geo (JSON) — then asks the
// browser for the rep's current position and AUTO-MARKS the rep attended
// (with a toast) when they're inside the fence. No tap required.
//
// Geolocation is requested lazily — we never prompt unless there's at
// least one in-window event with a fence. We auto-fire at most ONE check-in
// per (host event × portal session) so a flaky network doesn't loop, and
// the same-day dismissal set still prevents re-fire after a successful
// check-in even if the user reloads (BFF is also idempotent — see
// alreadyAttended).

const PRE_GRACE_MIN = 15;
const POST_GRACE_MIN = 30;
const STORAGE_KEY = "kindred:geofencePromptState:v1";

// Mirror of geofence.js#parseVenueGeo on the BFF. Reads the JSON Airtable
// long-text field; returns null for null/empty/garbage. Keep the two
// parsers in sync — the UI never trusts its own pre-check, the BFF
// re-validates on every self-check-in POST.
const parseVenueGeo = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object") return null;
    const lat = Number(parsed.lat);
    const lng = Number(parsed.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    let radiusM = Number(parsed.radiusM);
    if (!Number.isFinite(radiusM) || radiusM <= 0) radiusM = 75;
    if (radiusM > 5000) radiusM = 5000;
    return { lat, lng, radiusM };
  } catch (_) {
    return null;
  }
};

const haversineMeters = (a, b) => {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat));
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

// Bug fix: use CT calendar date (not UTC) so the dismissed set expires at
// midnight CT rather than midnight UTC, which can differ by up to 6 hours.
const ctToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());

const readDismissedSet = () => {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    const today = ctToday();
    if (!parsed || parsed.day !== today || !Array.isArray(parsed.ids)) return new Set();
    return new Set(parsed.ids);
  } catch (_) {
    return new Set();
  }
};

const writeDismissedSet = (set) => {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      day: ctToday(),
      ids: Array.from(set),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    // Quota / private-mode — silently skip; banner just re-prompts next load.
  }
};

const extractHostEventId = (event) => {
  // Mirrors getAllEvents on the BFF: events surface here either as the
  // host record or as the rep's own subtask. Host Event[0] points to the
  // host id when the row is a subtask; otherwise the row IS the host.
  const hostLink = event?.fields?.["Host Event"]?.[0];
  return hostLink || event?.id || null;
};

const isInWindow = (event, now) => {
  const startISO = event?.fields?.["Event Date & Time"];
  if (!startISO) return false;
  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) return false;
  const endISO = event?.fields?.["Event End Date & Time"];
  const end = endISO ? new Date(endISO) : new Date(start.getTime() + 60 * 60 * 1000);
  const windowStart = new Date(start.getTime() - PRE_GRACE_MIN * 60 * 1000);
  const windowEnd = new Date(end.getTime() + POST_GRACE_MIN * 60 * 1000);
  return now >= windowStart && now <= windowEnd;
};

const EventGeofencePrompt = () => {
  const token = useSelector((s) => s.auth?.token);
  // Skip the query entirely when not authed — saves an unauthenticated
  // /all-events POST on the public-share pages where this component is
  // still mounted by the App shell.
  const { data, refetch } = useGetEventsQuery(undefined, { skip: !token });
  const [selfCheckInEvent] = useSelfCheckInEventMutation();

  const [dismissed, setDismissed] = useState(() => readDismissedSet());
  const [activeBanner, setActiveBanner] = useState(null); // { hostRecordId, title, venue, position, distanceM }
  const [position, setPosition] = useState(null); // { lat, lng, accuracy }
  const positionRequestedRef = useRef(false);

  const events = data?.data || [];

  // Re-evaluate every minute so the banner appears/disappears around the
  // event window without needing a page reload.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Candidate events: in-window + venue geo present + not dismissed today
  // + not already Attended. We dedupe on host-id to avoid two cards for
  // the same event (e.g. host record + subtask).
  const candidates = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const event of events) {
      const hostRecordId = extractHostEventId(event);
      if (!hostRecordId || seen.has(hostRecordId)) continue;
      seen.add(hostRecordId);
      if (dismissed.has(hostRecordId)) continue;
      if (!isInWindow(event, now)) continue;
      const venue = parseVenueGeo(event?.fields?.["Venue Geo (JSON)"] || "");
      if (!venue) continue;
      // Skip if the attendance pill already says Attended/Present for us.
      const myStatus = (event?.fields?.["My Attendance Status"] || "").toLowerCase();
      if (myStatus === "attended" || myStatus === "present") continue;
      out.push({
        hostRecordId,
        title: event?.fields?.["Event Title"] || "this event",
        venue,
      });
    }
    return out;
  }, [events, dismissed, now]);

  // Lazy geolocation: only ask once we actually have at least one in-window
  // candidate this session, so non-event days never see the perms prompt.
  useEffect(() => {
    if (candidates.length === 0) return;
    if (positionRequestedRef.current) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    positionRequestedRef.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy || 0,
        });
      },
      () => {
        // Denied / unavailable / timed out — no UI surface. The rep just
        // stays unmarked until the host marks them, same as a rep without
        // geolocation permission today.
      },
      { enableHighAccuracy: true, maximumAge: 60 * 1000, timeout: 15 * 1000 }
    );
  }, [candidates]);

  // Pick the closest candidate that's actually inside its fence (plus a
  // capped accuracy slack matching the BFF). At most one banner at a time;
  // dismiss surfaces the next one (or nothing) on the next render.
  useEffect(() => {
    if (!position) { setActiveBanner(null); return; }
    let best = null;
    for (const c of candidates) {
      const d = haversineMeters(position, c.venue);
      const slack = Math.min(position.accuracy || 0, 150);
      if (d <= c.venue.radiusM + slack) {
        if (!best || d < best.distanceM) {
          best = { ...c, distanceM: Math.round(d) };
        }
      }
    }
    setActiveBanner(best);
  }, [candidates, position]);

  const dismiss = (hostRecordId) => {
    const next = new Set(dismissed);
    next.add(hostRecordId);
    setDismissed(next);
    writeDismissedSet(next);
    setActiveBanner(null);
  };

  // Per-session guard so a failed POST (or a flaky network during it)
  // doesn't loop on every render. On success, dismiss() also writes the
  // same-day localStorage entry, so reloads won't re-fire either.
  const autoFiredRef = useRef(new Set());

  const checkIn = useCallback(async (banner, pos) => {
    if (!banner || !pos) return;
    const { hostRecordId, title } = banner;
    try {
      const res = await selfCheckInEvent({
        hostRecordId,
        geo: {
          lat: pos.lat,
          lng: pos.lng,
          accuracy: pos.accuracy || 0,
        },
      }).unwrap();
      const alreadyAttended = res?.data?.alreadyAttended;
      toast.success(alreadyAttended ? "Already checked in." : `Auto-marked attended at ${title}.`);
      dismiss(hostRecordId);
      // Best-effort refresh in case invalidation hasn't propagated yet.
      try { refetch(); } catch (_) {}
    } catch (err) {
      const message = err?.data?.message || err?.message || "Couldn't auto-mark attended.";
      toast.error(message);
    }
  }, [selfCheckInEvent, dismiss, refetch]);

  // Auto-fire check-in as soon as we have BOTH an in-fence candidate AND a
  // position. One attempt per (host event × portal session); same-day
  // dismissal prevents re-fire after a successful POST across reloads.
  useEffect(() => {
    if (!activeBanner || !position) return;
    if (autoFiredRef.current.has(activeBanner.hostRecordId)) return;
    autoFiredRef.current.add(activeBanner.hostRecordId);
    checkIn(activeBanner, position);
    // checkIn() handles toast + dismiss; intentionally fire-and-forget.
  }, [activeBanner?.hostRecordId, position?.lat, position?.lng, checkIn]);

  // No UI — the toast is the rep's confirmation. The component still drives
  // the geolocation request + dismissal cooldown silently. posError is
  // intentionally not surfaced as a banner: a denied/unavailable location
  // just means the rep stays unmarked until they tap any other Mark Attended
  // path (host marks them, or future native-app background listener).
  return null;
};

export default EventGeofencePrompt;
